/**
 * Deterministic safety policy for native tools.
 *
 * One pure function decides whether a specific call may run, must be approved,
 * or is refused, and returns the reason that bound it. No IO, no clock, no
 * randomness: the same inputs always produce the same decision, so the answer
 * can be recomputed in the API, the Worker, and a test and never disagree.
 *
 * Every layer here only ever tightens, with exactly one deliberate exception:
 * `memberOutranksWorkspace` lets an owner or admin's personal choice replace
 * the workspace policy rather than stack under it, because that is a rule they
 * already control. See the field's own documentation for why that grants them
 * nothing they did not already have. Nothing loosens past the registry floor,
 * the kill switches, or the launch state under any input.
 */
import type { SpecLaunchState, SpecToolDecision } from '@parcel/types';
import type { NativeToolDefinition, NativeToolEffect } from './types.js';

export type SpecPolicyReason =
  | 'launch_off'
  | 'read_only'
  | 'kill_switch'
  | 'registry_floor'
  | 'workspace_policy'
  | 'member_policy'
  | 'default_policy'
  | 'unknown_tool'
  /** The member switched the chat's Web off (web_search only); set by the API on the run's policies. */
  | 'chat_switch'
  /** An enrichment spend under the card threshold; set by the API when it lifts a needs_approval floor. */
  | 'small_spend';

export interface EffectiveToolPolicy {
  decision: SpecToolDecision;
  reason: SpecPolicyReason;
  /** The floor that applied to THIS call, including any action-level guard. */
  registryFloor: SpecToolDecision;
  workspaceDecision: SpecToolDecision | null;
  memberDecision: SpecToolDecision | null;
}

/** Always allow < Needs approval < Blocked. */
export const STRICTNESS: Readonly<Record<SpecToolDecision, number>> = {
  always_allow: 0,
  needs_approval: 1,
  blocked: 2,
};

export function strictest(a: SpecToolDecision, b: SpecToolDecision): SpecToolDecision {
  return STRICTNESS[a] >= STRICTNESS[b] ? a : b;
}

export function reasonWithMemberPolicy<Reason extends string>(
  winner: { decision: SpecToolDecision; reason: Reason },
  withoutMember: { decision: SpecToolDecision; reason: Reason } | null,
  memberGranted: boolean,
): Reason | 'member_policy' {
  if (!withoutMember) return winner.reason;
  if (withoutMember.decision === winner.decision) return withoutMember.reason;
  return memberGranted ? 'member_policy' : winner.reason;
}

/**
 * A stored decision is only honored if it is one of the three known states.
 * A row carrying anything else (a hand-edited value, a future enum member an
 * older deploy has not learned yet) is treated as Blocked rather than ignored.
 */
const KNOWN_DECISIONS = new Set<string>(['always_allow', 'needs_approval', 'blocked']);

export function readStoredDecision(value: SpecToolDecision | null): SpecToolDecision | null {
  if (value === null || value === undefined) return null;
  // Set membership, not `in`: `'toString' in STRICTNESS` is true through the
  // prototype chain, which would let a junk value resolve to a function and
  // make every strictness comparison silently false.
  return KNOWN_DECISIONS.has(value) ? value : 'blocked';
}

/** The immutable floor implied by what a call actually does. */
const FLOOR_BY_EFFECT: Readonly<Record<NativeToolEffect, SpecToolDecision>> = {
  read: 'always_allow',
  reversible_write: 'needs_approval',
  destructive: 'blocked',
  admin_security: 'blocked',
};

/** Exported so every evaluator (native and connection) derives the same floor from an effect,
    rather than each holding its own copy of the mapping. */
export function floorForEffect(effect: NativeToolEffect): SpecToolDecision {
  // An effect this build does not know is not assumed harmless.
  return FLOOR_BY_EFFECT[effect] ?? 'blocked';
}

/**
 * What an explicit personal `always_allow` answers.
 *
 * `needs_approval` from a floor or a shipped default means "ask before the
 * first one", and a member who granted the tool has answered it. `blocked`
 * means "not at all", which nobody self-grants. Because the effect is resolved
 * per call from validated arguments, a grant on a multiplexed tool cannot carry
 * into its destructive sub-operation: that resolves `blocked` and stays there.
 *
 * Exported so the connection evaluator applies the identical rule rather than
 * growing a second interpretation of the same grant.
 */
export function answeredByGrant(decision: SpecToolDecision, granted: boolean): SpecToolDecision {
  return granted && decision === 'needs_approval' ? 'always_allow' : decision;
}

const BLOCKED_UNKNOWN_TOOL: EffectiveToolPolicy = {
  decision: 'blocked',
  reason: 'unknown_tool',
  registryFloor: 'blocked',
  workspaceDecision: null,
  memberDecision: null,
};

export interface NativeToolPolicyInput {
  tool: NativeToolDefinition | null;
  args: Readonly<Record<string, unknown>>;
  launchState: SpecLaunchState;
  toolExecutionEnabled: boolean;
  nativeWritesEnabled: boolean;
  workspaceDecision: SpecToolDecision | null;
  memberDecision: SpecToolDecision | null;
  /**
   * True when this member's personal choice REPLACES the workspace policy
   * instead of stacking under it, which is the case for an owner or admin.
   *
   * This is the one input that can produce a looser result than the workspace
   * configured, so it is worth being exact about why it is safe. An owner or
   * admin can already edit the workspace policy itself: the same person could
   * unblock a tool workspace-wide, use it, and re-block it. Letting them loosen
   * only for themselves is strictly LESS reach than they already have, and it
   * leaves everyone else's ceiling untouched.
   *
   * It never escapes the registry floor, the kill switches, or the launch
   * state: those are separate candidates below and still apply. So the widest
   * a personal override can go is the tool's own floor, which is exactly the
   * bound `blockedOperation` tools rely on to stay unusable.
   *
   * Must be derived from the member's CURRENT role at evaluation time, never
   * from anything recorded when the override was written. A stored flag would
   * keep honoring an admin's loosening after they were demoted.
   */
  memberOutranksWorkspace?: boolean;
}

export function evaluateNativeToolPolicy(input: NativeToolPolicyInput): EffectiveToolPolicy {
  const { tool } = input;
  if (!tool) return BLOCKED_UNKNOWN_TOOL;

  // The effect of THIS call. Multiplexed tools classify from validated
  // arguments, so a broad tool-level choice cannot smuggle a destructive
  // sub-operation past its own floor.
  const effect = tool.resolveEffect ? tool.resolveEffect(input.args) : tool.effect;
  const floor = strictest(tool.registryFloor, floorForEffect(effect));

  const workspaceDecision = readStoredDecision(input.workspaceDecision);
  const memberDecision = readStoredDecision(input.memberDecision);

  // Candidates in reason precedence order. The strictest wins; ties resolve to
  // whichever appears first here, which is the constraint the user can do least
  // about, so the explanation names the real blocker.
  const candidates: Array<{ decision: SpecToolDecision; reason: SpecPolicyReason }> = [];

  if (input.launchState === 'off') candidates.push({ decision: 'blocked', reason: 'launch_off' });
  if (input.launchState === 'read_only') {
    // History and settings stay readable, but no tool call starts.
    candidates.push({ decision: 'blocked', reason: 'read_only' });
  }
  if (!input.toolExecutionEnabled) candidates.push({ decision: 'blocked', reason: 'kill_switch' });
  if (!input.nativeWritesEnabled && effect !== 'read') {
    candidates.push({ decision: 'blocked', reason: 'kill_switch' });
  }

  // A grant lifts the prompt, never a block. The kill switches above and the
  // workspace rule below stay candidates either way, and strictest-wins means
  // either can put the prompt back.
  const granted = memberDecision === 'always_allow';
  candidates.push({ decision: answeredByGrant(floor, granted), reason: 'registry_floor' });
  // An owner/admin's own choice REPLACES the workspace policy rather than
  // joining it, so their personal setting is not re-tightened by the workspace
  // rule they themselves control. Everyone else's personal choice stacks, and
  // strictest-wins means it can only tighten. The floor and the kill switches
  // above are still candidates either way, so nothing here reaches past them.
  const personalReplacesWorkspace =
    Boolean(input.memberOutranksWorkspace) && memberDecision !== null;
  if (workspaceDecision && !personalReplacesWorkspace) {
    candidates.push({ decision: workspaceDecision, reason: 'workspace_policy' });
  }
  if (memberDecision) candidates.push({ decision: memberDecision, reason: 'member_policy' });
  candidates.push({
    decision: answeredByGrant(tool.defaultPolicy, granted),
    reason: 'default_policy',
  });

  let winner = candidates[0] as { decision: SpecToolDecision; reason: SpecPolicyReason };
  for (const candidate of candidates) {
    if (STRICTNESS[candidate.decision] > STRICTNESS[winner.decision]) winner = candidate;
  }

  // Attribute the member layer only when removing it changes the answer.
  const withoutMember =
    memberDecision !== null ? evaluateNativeToolPolicy({ ...input, memberDecision: null }) : null;
  const reason = reasonWithMemberPolicy(winner, withoutMember, granted);

  return {
    decision: winner.decision,
    reason,
    registryFloor: floor,
    workspaceDecision,
    memberDecision,
  };
}
